import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChannelFormData } from './ChannelModal';
import { RuleWizardModal, RuleFormData, RuleType } from './RuleWizardModal';

// ---------------------------------------------------------
// Auto Notification Rules Section
// ---------------------------------------------------------

export const AutoNotificationRulesSection = memo(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { watch, setValue } = useFormContext<ChannelFormData>();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);

  const strCreateRule = t('blox.notifier.channel.rules.create_rule');
  const strEditRule = t('blox.notifier.channel.rules.edit_rule');
  const strDeleteRule = t('blox.notifier.channel.rules.delete_rule');
  const strNoRules = t('blox.notifier.channel.rules.no_rules');
  const strAssetType = t('blox.notifier.channel.rules.asset_type');
  const strFlags = t('blox.notifier.channel.rules.flags');
  const strKeywords = t('blox.notifier.channel.rules.keyword_tags');
  const strSections = t('blox.notifier.channel.rules.section_tags');

  const watchedPublishRules = watch('publish_rules');
  const publishRules = useMemo(() => watchedPublishRules || [], [watchedPublishRules]);

  // Determine which rule types are already in use (for filtering in wizard)
  const usedRuleTypes = useMemo<RuleType[]>(
    () => publishRules.map((rule) => rule.type),
    [publishRules],
  );

  const allRuleTypesUsed = usedRuleTypes.length >= 4;

  const getRuleTypeLabel = (type: string) => {
    switch (type) {
      case 'assetType':
        return strAssetType;
      case 'flags':
        return strFlags;
      case 'keywords':
        return strKeywords;
      case 'sections':
        return strSections;
      default:
        return type;
    }
  };

  const handleAddRule = () => {
    setEditingRuleIndex(null);
    setWizardOpen(true);
  };

  const handleEditRule = (index: number) => {
    setEditingRuleIndex(index);
    setWizardOpen(true);
  };

  const handleDeleteRule = (index: number) => {
    const updatedRules = publishRules.filter((_, i) => i !== index);
    setValue('publish_rules', updatedRules, { shouldDirty: true });
  };

  const handleSaveRule = useCallback(
    (ruleData: RuleFormData) => {
      const newRule = {
        name: ruleData.name,
        type: ruleData.type,
        values: ruleData.values,
      };

      if (editingRuleIndex !== null) {
        // Update existing rule
        const updatedRules = [...publishRules];
        updatedRules[editingRuleIndex] = newRule;
        setValue('publish_rules', updatedRules, { shouldDirty: true });
      } else {
        // Add new rule
        setValue('publish_rules', [...publishRules, newRule], { shouldDirty: true });
      }

      setWizardOpen(false);
      setEditingRuleIndex(null);
    },
    [editingRuleIndex, publishRules, setValue],
  );

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setEditingRuleIndex(null);
  };

  const editingRule = editingRuleIndex !== null ? publishRules[editingRuleIndex] : null;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle2">
          {t('blox.notifier.channel.rules.title')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddRule}
          disabled={allRuleTypesUsed}
        >
          {strCreateRule}
        </Button>
      </Stack>

      {publishRules.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {strNoRules}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {publishRules.map((rule, index) => (
            <Box
              key={`rule-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {getRuleTypeLabel(rule.type)}
                  </Typography>
                  {rule.name && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      ({rule.name})
                    </Typography>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  sx={{
                    flexWrap: 'wrap',
                    mt: 1,
                  }}
                >
                  {rule.values.map((value, idx) => (
                    <Chip
                      key={`${value}-${idx}`}
                      label={value}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ flexShrink: 0 }}
              >
                <Tooltip title={strEditRule}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditRule(index)}
                    aria-label={strEditRule}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={strDeleteRule}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteRule(index)}
                    aria-label={strDeleteRule}
                    sx={{
                      '&:hover': { color: theme.palette.error.main },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* Rule Wizard Modal */}
      <RuleWizardModal
        open={wizardOpen}
        onClose={handleCloseWizard}
        onSave={handleSaveRule}
        usedRuleTypes={
          editingRuleIndex !== null
            ? usedRuleTypes.filter((rt) => rt !== publishRules[editingRuleIndex]?.type)
            : usedRuleTypes
        }
        initialData={
          editingRule
            ? {
                name: editingRule.name || '',
                type: editingRule.type,
                values: editingRule.values,
              }
            : undefined
        }
        isEdit={editingRuleIndex !== null}
      />
    </Box>
  );
});
AutoNotificationRulesSection.displayName = 'AutoNotificationRulesSection';
